import { type Editor, useEditorState } from "@tiptap/react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { ExternalLinkIcon, LinkIcon } from "./icons.tsx";

/*
 * Links in a draft, added, changed and removed in the page (`docs/2026-10-04-editor-links.md`). Both
 * editors mount the same three pieces: `useEditorLinks` holds the state, `LinkButton` sits in the
 * formatting toolbar, and `LinkLayer` goes directly after the editor's content, where it draws the
 * address field or, with the caret inside a link, that link's address with Edit and Remove.
 *
 * Nothing here decides what a draft may link to. The host's own link checks run at approval as they
 * always did; this only keeps a rep from typing an address those checks could never accept.
 */

export type LinkHref =
  | { readonly ok: true; readonly href: string }
  | { readonly ok: false; readonly message: string };

const refuse = (message: string): LinkHref => ({ ok: false, message });
const EMAIL = /^[^\s@/:?#]+@[^\s@/:?#]+\.[^\s@/:?#]+$/;
/** A scheme, but not the `host:8443` of an address typed without one. */
const SCHEME = /^([a-z][a-z0-9+.-]*):(?!\d+(?:[/?#]|$))/i;

/** The scheme an address was written with, lower-cased, or null when it has none. */
export function linkScheme(raw: string): string | null {
  return SCHEME.exec(raw.trim())?.[1]?.toLowerCase() ?? null;
}

/**
 * The address a rep typed, as the link it becomes: `https://` or `mailto:` only. An address typed
 * without a scheme is given `https://`, and a bare email address `mailto:`. Anything else is refused
 * with a sentence the field shows.
 */
export function normalizeLinkHref(raw: string): LinkHref {
  const text = raw.trim();
  if (text === "") return refuse("Type or paste the address this should link to.");
  if (/\s/.test(text)) return refuse("An address cannot contain spaces. Check what was pasted.");
  if (EMAIL.test(text)) return { ok: true, href: `mailto:${text}` };
  const scheme = SCHEME.exec(text)?.[1]?.toLowerCase();
  if (scheme === "mailto") {
    const address = text.slice("mailto:".length).split("?")[0] ?? "";
    return EMAIL.test(address)
      ? { ok: true, href: text }
      : refuse("That is not an email address. Check the part after mailto:.");
  }
  if (scheme === "http")
    return refuse("Use https:// for this address. A plain http:// link is not added.");
  if (scheme !== undefined && scheme !== "https")
    return refuse("Only https:// web addresses and mailto: email addresses can be linked.");
  if (text.startsWith("/"))
    return refuse("That is not a full web address. Start it with https://.");
  const href = scheme === "https" ? text : `https://${text}`;
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return refuse("That is not a web address. It should look like https://company.com/page.");
  }
  if (!url.hostname.includes(".") || url.username !== "" || url.password !== "")
    return refuse("That is not a web address. It should look like https://company.com/page.");
  return { ok: true, href };
}

type LinkForm = { readonly mode: "add" | "edit"; readonly value: string; readonly error: string };

export type EditorLinks = {
  readonly editor: Editor | null;
  /** The caret or selection is inside a link. */
  readonly inLink: boolean;
  readonly href: string;
  readonly form: LinkForm | null;
  readonly bubble: boolean;
  readonly open: () => void;
  readonly change: (value: string) => void;
  readonly apply: () => void;
  readonly cancel: () => void;
  readonly remove: () => void;
  readonly layer: React.RefObject<HTMLDivElement | null>;
};

/** The link state of one editor: what the caret is in, and the address field when it is open. */
export function useEditorLinks(editor: Editor | null): EditorLinks {
  const [form, setForm] = useState<LinkForm | null>(null);
  const [focused, setFocused] = useState(false);
  const layer = useRef<HTMLDivElement | null>(null);
  const at = useEditorState({
    editor,
    selector: ({ editor: current }) =>
      current
        ? {
            inLink: current.isActive("link"),
            href: String(current.getAttributes("link")["href"] ?? ""),
            from: current.state.selection.from,
          }
        : null,
  });
  const inLink = at?.inLink ?? false;
  const href = at?.href ?? "";

  const open = (): void => {
    if (!editor) return;
    const editing = editor.isActive("link");
    const current = String(editor.getAttributes("link")["href"] ?? "");
    setForm({ mode: editing ? "edit" : "add", value: editing ? current : "", error: "" });
  };
  const cancel = (): void => {
    setForm(null);
    editor?.commands.focus();
  };
  const apply = (): void => {
    if (!editor || !form) return;
    const result = normalizeLinkHref(form.value);
    if (!result.ok) {
      setForm({ ...form, error: result.message });
      return;
    }
    const chain = editor.chain().focus();
    if (editor.isActive("link")) chain.extendMarkRange("link").setLink({ href: result.href }).run();
    else if (editor.state.selection.empty)
      // Nothing selected: the address is its own text, and what is typed next is not part of it.
      chain
        .insertContent({
          type: "text",
          text: result.href.replace(/^mailto:/i, ""),
          marks: [{ type: "link", attrs: { href: result.href } }],
        })
        .unsetMark("link")
        .run();
    else chain.setLink({ href: result.href }).run();
    setForm(null);
  };
  const remove = (): void => {
    editor?.chain().focus().extendMarkRange("link").unsetLink().run();
    setForm(null);
  };

  // The key handler is attached once and must open the field for the selection as it is now.
  const openNow = useRef(open);
  openNow.current = open;
  useEffect(() => {
    if (!editor) return;
    const inEditor = (target: EventTarget | null): boolean => {
      if (editor.isDestroyed || !(target instanceof Node)) return false;
      try {
        return editor.view.dom.contains(target);
      } catch {
        // Not mounted yet, so nothing is inside it.
        return false;
      }
    };
    const onFocus = (): void => setFocused(true);
    // Focus inside the layer keeps it; focus anywhere else closes the field, and outside the editor
    // too, the bubble. Focus that went nowhere (another window) changes nothing.
    const onFocusIn = (event: FocusEvent): void => {
      if (!(event.target instanceof Node)) return;
      if (layer.current?.contains(event.target)) {
        setFocused(true);
        return;
      }
      setForm(null);
      if (!inEditor(event.target)) setFocused(false);
    };
    const onBlur = ({ event }: { event: FocusEvent }): void => {
      const next = event.relatedTarget;
      if (next instanceof Node && layer.current?.contains(next)) return;
      setFocused(false);
    };
    const onKey = (event: KeyboardEvent): void => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== "k")
        return;
      if (!inEditor(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      openNow.current();
    };
    // The view is mounted by the time a key is pressed inside it; the listener itself need not wait.
    document.addEventListener("keydown", onKey, true);
    document.addEventListener("focusin", onFocusIn);
    editor.on("focus", onFocus);
    editor.on("blur", onBlur);
    return () => {
      editor.off("focus", onFocus);
      editor.off("blur", onBlur);
      document.removeEventListener("keydown", onKey, true);
      document.removeEventListener("focusin", onFocusIn);
    };
  }, [editor]);

  // The field takes the focus, and with it the browser's own selection: keep what is being linked marked.
  const marking = form !== null;
  useEffect(() => {
    if (!marking || !editor || typeof CSS === "undefined" || !("highlights" in CSS)) return;
    const { from, to } = editor.state.selection;
    if (from === to) return;
    try {
      const start = editor.view.domAtPos(from);
      const end = editor.view.domAtPos(to);
      const range = document.createRange();
      range.setStart(start.node, start.offset);
      range.setEnd(end.node, end.offset);
      CSS.highlights.set("lcr-link-target", new Highlight(range));
    } catch {
      // A selection the view cannot map is simply not marked.
    }
    return () => {
      CSS.highlights.delete("lcr-link-target");
    };
  }, [marking, editor]);

  const bubble = form === null && focused && inLink;
  // Which float is drawn. The field and the bubble differ in width, so each is placed afresh.
  const float = form ? form.mode : bubble ? "bubble" : null;
  const caret = at?.from ?? 0;
  // Under the caret, kept inside the editor's width. Without a layout there is nothing to measure.
  useLayoutEffect(() => {
    const anchor = layer.current;
    const element = anchor?.firstElementChild;
    if (float === null || !editor || !anchor || !(element instanceof HTMLElement)) return;
    try {
      const at = editor.view.coordsAtPos(caret);
      const box = anchor.getBoundingClientRect();
      if (box.width === 0) return;
      const room = Math.max(0, box.width - element.offsetWidth);
      element.style.setProperty("--lcr-link-top", `${Math.round(at.bottom - box.top + 8)}px`);
      element.style.setProperty(
        "--lcr-link-left",
        `${Math.round(Math.min(Math.max(0, at.left - box.left - 12), room))}px`,
      );
      element.scrollIntoView?.({ block: "nearest" });
    } catch {
      // A position the view cannot map keeps the fallback place under the editor.
    }
  }, [float, caret, editor]);

  return {
    editor,
    inLink,
    href,
    form,
    bubble,
    open,
    change: (value) => setForm((current) => (current ? { ...current, value, error: "" } : current)),
    apply,
    cancel,
    remove,
    layer,
  };
}

const MOD_KEY =
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform) ? "⌘" : "Ctrl+";

/**
 * The toolbar's link button. It opens the address field; it never asks the browser. `disabled`: the
 * draft is held while it may be replaced (`useRedraftAsking`).
 */
export function LinkButton({
  links,
  disabled = false,
}: {
  links: EditorLinks;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      aria-label="Link"
      aria-haspopup="dialog"
      aria-pressed={links.inLink}
      title={`Link (${MOD_KEY}K)`}
      disabled={disabled || !links.editor}
      onClick={links.open}
    >
      <LinkIcon size={16} />
    </button>
  );
}

/** A press on the bubble must not take the caret out of the link, or the bubble is gone before the click. */
const keepCaret = (event: React.MouseEvent): void => event.preventDefault();

/** Goes directly after the editor's content: the address field, or the bubble for the link under the caret. */
export function LinkLayer({ links }: { links: EditorLinks }) {
  const { form } = links;
  const input = useRef<HTMLInputElement | null>(null);
  const id = useId();
  const mode = form?.mode ?? null;
  useEffect(() => {
    if (mode === null) return;
    input.current?.focus();
    input.current?.select();
  }, [mode]);
  const openable = /^(https?:|mailto:)/i.test(links.href);
  const display = links.href.replace(/^(https?:\/\/|mailto:)/i, "");
  return (
    <div className="lcr-link-layer" ref={links.layer}>
      {form ? (
        <div
          key="field"
          className="lcr-link-popover"
          role="dialog"
          aria-label={form.mode === "edit" ? "Edit link" : "Add link"}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              links.cancel();
            } else if (event.key === "Enter" && event.target === input.current) {
              event.preventDefault();
              event.stopPropagation();
              links.apply();
            }
          }}
        >
          <label htmlFor={id}>Link address</label>
          <div className="lcr-link-row">
            <input
              id={id}
              ref={input}
              type="text"
              inputMode="url"
              autoComplete="off"
              autoCapitalize="off"
              spellCheck={false}
              placeholder="https://"
              value={form.value}
              aria-invalid={form.error !== ""}
              aria-describedby={form.error ? `${id}-error` : undefined}
              onChange={(event) => links.change(event.target.value)}
            />
            <button type="button" className="lcr-small-button" onClick={links.apply}>
              Apply
            </button>
            <button type="button" className="lcr-small-button lcr-ghost" onClick={links.cancel}>
              Cancel
            </button>
          </div>
          {form.error && (
            <p className="lcr-link-error" id={`${id}-error`} role="alert">
              {form.error}
            </p>
          )}
          {form.mode === "edit" && (
            <button type="button" className="lcr-link-unlink" onClick={links.remove}>
              Remove link
            </button>
          )}
        </div>
      ) : links.bubble ? (
        <div key="bubble" className="lcr-link-bubble" role="toolbar" aria-label="Link">
          {openable ? (
            <a
              className="lcr-link-address"
              href={links.href}
              target="_blank"
              rel="noopener noreferrer"
              title={links.href}
              aria-label={`Open ${links.href} in a new tab`}
              onMouseDown={keepCaret}
            >
              <span className="lcr-link-text">{display}</span>
              <ExternalLinkIcon size={14} />
            </a>
          ) : (
            <span className="lcr-link-address" title={links.href}>
              <span className="lcr-link-text">{display}</span>
            </span>
          )}
          <button
            type="button"
            className="lcr-small-button lcr-ghost"
            onMouseDown={keepCaret}
            onClick={links.open}
          >
            Edit
          </button>
          <button
            type="button"
            className="lcr-small-button lcr-ghost"
            onMouseDown={keepCaret}
            onClick={links.remove}
          >
            Remove
          </button>
        </div>
      ) : null}
    </div>
  );
}
